import React, { useEffect, useState } from 'react';
import { clsx } from 'clsx';

export const ProductTileQuickAdd = ({ product, isHovered, settings, isMobile, onVariantChange, onAddToCart }) => {
  const [currentVariant, setCurrentVariant] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const {
    classes,
    quick_add_display,
    always_show_quick_add_mobile,
    button_text,
    button_text_style,
    uppercase,
    button_height,
    bottom_position,
    button_radius,
    border_radius,
    width_type,
    width_percentage,
    width_pixels,
    padding_top,
    padding_right,
    padding_bottom,
    padding_left,
    margin_top,
    margin_right,
    margin_bottom,
    margin_left
  } = settings;

  const buttonRadius = button_radius;
  const borderRadius = border_radius;

  const getQuickAddBorderRadiusClasses = () => {
    if (buttonRadius === 'rounded') {
      return 'rounded-full';
    } else if (buttonRadius === 'set_radius') {
      return `rounded-[${borderRadius}px]`;
    }
  };

  const getPaddingMarginClasses = () => {
    return `
      pt-[${padding_top}px]
      pr-[${padding_right}px]
      pb-[${padding_bottom}px]
      pl-[${padding_left}px]
      mt-[${margin_top}px]
      mr-[${margin_right}px]
      mb-[${margin_bottom}px]
      ml-[${margin_left}px]
    `;
  };

  const widthClasses = () => {
    if (width_type === 'set_width') {
      return `w-[${width_pixels}px]`;
    } else {
      return `w-[${width_percentage}%]`;
    }
  };

  useEffect(() => {
    if (!isHovered) {
      setCurrentVariant(product.variants[0]);
    }

    if (isHovered && product?.variants?.length <= 1) {
      setCurrentVariant(product.variants[0]);
    }
  }, [isHovered]);

  // Listen for cart loading events
  useEffect(() => {
    const handleLoadingStart = (event) => {
      // Only show loading if this variant is being added
      if (event.detail.variantId === currentVariant?.id) {
        setIsLoading(true);
      }
    };

    const handleLoadingEnd = (event) => {
      // Only hide loading if this variant was being added
      if (event.detail.variantId === currentVariant?.id) {
        setIsLoading(false);
      }
    };

    window.addEventListener('cart:add_loading_start', handleLoadingStart);
    window.addEventListener('cart:add_loading_end', handleLoadingEnd);

    return () => {
      window.removeEventListener('cart:add_loading_start', handleLoadingStart);
      window.removeEventListener('cart:add_loading_end', handleLoadingEnd);
    };
  }, [currentVariant?.id]);

  const updateCurrentVariant = (variant) => {
    setCurrentVariant(variant);

    // Call the prop callback if provided
    if (onVariantChange) {
      onVariantChange(variant, product);
    }
  };

  const handleAddToCart = (e) => {
    // If custom onAddToCart prop is provided, use it instead of default form submission
    if (onAddToCart) {
      e.preventDefault();
      onAddToCart(currentVariant, product);
      return;
    }

    // Default behavior: let the form submit normally to /cart/add
  };

  const getVisibilityClasses = () => {
    // Show all the time if mobile and always_show_quick_add_mobile is true
    if ((isMobile && always_show_quick_add_mobile) || quick_add_display === 'always') {
      return '';
    } else {
      return 'opacity-0 quick-add-parent';
    }
  };

  return (
    <>
      {product?.variants?.length > 1 ? (
        <div
          className={clsx(
            'flex justify-center w-full absolute cursor-default quick-add',
            getVisibilityClasses(),
            getPaddingMarginClasses()
          )}
          style={{
            height: `${button_height}px`,
            bottom: `${bottom_position}px`
          }}
        >
          {!currentVariant && (
            <div
              className={clsx(
                'productcard__quick-add flex flex-row space-x-4 justify-center items-center',
                getQuickAddBorderRadiusClasses(),
                widthClasses()
              )}
              style={{ background: 'rgba(255, 255, 255, 0.40)' }}
            >
              {product?.variants?.map((variant) => (
                <React.Fragment key={variant?.id}>
                  {variant?.available ? (
                    <button
                      className='flex b__quick_option cursor-pointer'
                      onClick={() => updateCurrentVariant(variant)}
                    >
                      {(variant?.option1 && variant?.option1 !== '') ||
                      (Array.isArray(variant?.options) && variant?.options[0])
                        ? variant?.option1 || variant?.options[0]
                        : variant?.title}
                    </button>
                  ) : (
                    <div className='flex b__quick_option text-black/20 cursor-default'>
                      {(variant?.option1 && variant?.option1 !== '') ||
                      (Array.isArray(variant?.options) && variant?.options[0])
                        ? variant?.option1 || variant?.options[0]
                        : variant?.title}
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          )}

          {currentVariant && (
            <ajax-cart-product-form
              className={clsx(
                'flex space-x-[28px] justify-center items-center bg-white',
                getQuickAddBorderRadiusClasses(),
                widthClasses()
              )}
              style={{ height: `${button_height}px` }}
            >
              <form
                action='/cart/add'
                id='product_form_id'
                className='product-form w-full flex'
                noValidate
                data-prodify-product-form
                onSubmit={handleAddToCart}
              >
                <div className='flex w-full space-x-4 justify-center b5--regular text-white'>
                  <div className='flex'>
                    <p className='uppercase'>
                      Size&nbsp;:&nbsp;
                      {currentVariant.option1 ||
                        (currentVariant.options && currentVariant.options[0]) ||
                        currentVariant.title}
                      &nbsp;
                    </p>
                    <button
                      onClick={() => updateCurrentVariant(null)}
                      className={'underline'}
                    >
                      CHANGE
                    </button>
                  </div>
                  <div className='text-black/20'>|</div>
                  <input
                    type='hidden'
                    name='id'
                    value={`${currentVariant.id}`}
                  />
                  <button
                    type='submit'
                    disabled={isLoading}
                    className={`${button_text_style}${uppercase ? ' uppercase' : ''} ${isLoading ? 'opacity-50 cursor-not-allowed' : ''} relative flex items-center justify-center`}
                  >
                    {isLoading ? (
                      <>
                        <div className="animate-spin -ml-1 mr-2 h-4 w-4 border-2 border-current border-t-transparent rounded-full"></div>
                        Loading...
                      </>
                    ) : (
                      button_text
                    )}
                  </button>
                </div>

                <div
                  data-ajax-cart-errors='form'
                  className='text-xs text-error-1 mt-2'
                ></div>
              </form>
            </ajax-cart-product-form>
          )}
        </div>
      ) : (
        <div
          className={clsx(
            'flex justify-center w-full absolute cursor-pointer',
            getVisibilityClasses(),
            getPaddingMarginClasses()
          )}
          style={{
            height: `${button_height}px`,
            bottom: `${bottom_position}px`
          }}
        >
          {currentVariant && (
            <button
              className={clsx(
                `button button-primary ${classes} ${button_text_style}${uppercase ? ' uppercase' : ''} ${isLoading ? 'opacity-50 cursor-not-allowed' : ''} flex items-center justify-center`,
                getQuickAddBorderRadiusClasses(),
                widthClasses()
              )}
              style={{ height: `${button_height}px` }}
              onClick={handleAddToCart}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <div className="animate-spin -ml-1 mr-2 h-4 w-4 border-2 border-current border-t-transparent rounded-full"></div>
                  Loading...
                </>
              ) : (
                button_text
              )}
            </button>
          )}
        </div>
      )}
    </>
  );
};

export default ProductTileQuickAdd;
